/**
 * Route configuration with metadata for breadcrumbs and SEO
 */

export interface RouteConfig {
  path: string;
  label: string;
  title: string; // For document title
  description?: string; // For meta description
  parent?: string; // Parent route path for breadcrumbs
  breadcrumbResolver?: (loaderData: Record<string, unknown>) => string; // Function to resolve dynamic breadcrumb label
}

export const routeConfig: Record<string, RouteConfig> = {
  '/': {
    path: '/',
    label: 'Home',
    title: 'DomainBoost',
    description: 'Professional domain boost and SEO services',
  },
  '/dashboard': {
    path: '/dashboard',
    label: 'Dashboard',
    title: 'Dashboard - DomainBoost',
    description: 'View your domain boost statistics and performance metrics',
  },
  '/browse-sites': {
    path: '/browse-sites',
    label: 'Browse Sites',
    title: 'Browse Sites - DomainBoost',
    description: 'Discover high-quality websites for domain boosting',
  },
  '/packages': {
    path: '/packages',
    label: 'Packages',
    title: 'Packages - DomainBoost',
    description: 'Subscription packages for ongoing domain boost services',
  },
  '/orders': {
    path: '/orders',
    label: 'Orders',
    title: 'Orders - DomainBoost',
    description: 'Manage your domain boost orders',
  },
  '/content-orders': {
    path: '/content-orders',
    label: 'Content Orders',
    title: 'Content Orders - DomainBoost',
    description: 'Manage your content creation orders',
  },
  '/subscriptions': {
    path: '/subscriptions',
    label: 'Subscriptions',
    title: 'Subscriptions - DomainBoost',
    description: 'Manage your marketplace subscription plans',
  },
  '/wallet': {
    path: '/wallet',
    label: 'Wallet',
    title: 'Wallet - DomainBoost',
    description: 'Manage your balance and payment methods',
  },
  '/profile': {
    path: '/profile',
    label: 'Profile & Settings',
    title: 'Profile & Settings - DomainBoost',
    description: 'Manage your account profile and settings',
  },
  '/notifications': {
    path: '/notifications',
    label: 'Notifications',
    title: 'Notifications - DomainBoost',
    description: 'Manage your notification preferences',
  },
  '/help-center': {
    path: '/help-center',
    label: 'Help Center',
    title: 'Help Center - DomainBoost',
    description: 'Get help and support for your domain boost services',
  },
  '/help-articles': {
    path: '/help-articles',
    label: 'Help Articles',
    title: 'Help Articles - DomainBoost',
    description: 'Guides and how-tos for the DomainBoost platform',
  },
  '/faqs': {
    path: '/faqs',
    label: 'FAQs',
    title: 'FAQs - DomainBoost',
    description: 'Frequently asked questions about the DomainBoost platform',
  },
  '/login': {
    path: '/login',
    label: 'Login',
    title: 'Login - DomainBoost',
    description: 'Sign in to your DomainBoost account',
  },
  '/register': {
    path: '/register',
    label: 'Register',
    title: 'Register - DomainBoost',
    description: 'Create a new DomainBoost account',
  },
  '/forgot-password': {
    path: '/forgot-password',
    label: 'Forgot Password',
    title: 'Forgot Password - DomainBoost',
    description: 'Reset your DomainBoost account password',
  },
  '/reset-password': {
    path: '/reset-password',
    label: 'Reset Password',
    title: 'Reset Password - DomainBoost',
    description: 'Set a new password for your account',
  },
  // Dynamic routes
  '/help-articles/:slug': {
    path: '/help-articles/:slug',
    label: 'Article', // Default; overridden by breadcrumbResolver.
    title: 'Article - DomainBoost',
    description: 'DomainBoost help article',
    parent: '/help-articles',
    breadcrumbResolver: (loaderData: Record<string, unknown>) => {
      const article = loaderData?.article as Record<string, unknown> | undefined;
      return (article?.title as string) ?? 'Article';
    },
  },
};

/**
 * Get breadcrumb trail for a given path
 */
export function getBreadcrumbs(pathname: string): RouteConfig[] {
  const breadcrumbs: RouteConfig[] = [];
  const config = routeConfig[pathname];

  if (!config) {
    return breadcrumbs;
  }

  // Build breadcrumb trail
  let currentPath = pathname;
  const trail: RouteConfig[] = [];

  while (currentPath) {
    const current = routeConfig[currentPath];
    if (current) {
      trail.unshift(current);
      currentPath = current.parent ?? '';
    } else {
      break;
    }
  }

  return trail;
}

/**
 * Get route config for a given path
 */
export function getRouteConfig(pathname: string): RouteConfig | undefined {
  return routeConfig[pathname];
}
